import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useFilters } from "@/context/FilterContext";
import KpiCard from "@/components/dashboard/KpiCard";
import Gauge from "@/components/dashboard/GaugeChart";
import SubsystemMatrix, { StatusPill, SubsystemNameCell, SUBSYSTEM_COLORS } from "@/components/dashboard/SubsystemMatrix";
import { TrendingUp, AlertTriangle, Clock, Filter as FilterIcon } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, BarChart, Bar, Legend } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

function Panel({ n, title, right, children, className = "" }) {
  return (
    <div className={`bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <span className="text-[#93c5fd] mr-2 font-black text-lg">{n}.</span>
          <span className="text-[#1e40af] font-bold text-sm uppercase tracking-tight">{title}</span>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

const FILTER_OPTIONS = [
  { value: "all", label: "All Delays" },
  { value: "lt1", label: "< 1 Menit" },
  { value: "gt1", label: "> 1 Menit" },
  { value: "gt3", label: "> 3 Menit" },
];

export default function Reliability() {
  const { params } = useFilters();
  const [summary, setSummary] = useState(null);
  const [mtbfTrend, setMtbfTrend] = useState({ items: [], granularity: "daily", mtbf_delay_filter: "all", mtbf_delay_filter_label: "All Delays" });
  const [pareto, setPareto] = useState({ items: [] });
  const [matrix, setMatrix] = useState({ items: [], targets: {} });
  const [failures, setFailures] = useState([]);
  const [settings, setSettings] = useState(null);
  const [tick, setTick] = useState(0);

  const activeFilter = settings?.mtbf_delay_filter || "all";

  useEffect(() => {
    (async () => {
      const s = await api.get("/settings"); setSettings(s.data);
    })();
    const h = () => { setTick(t => t + 1); api.get("/settings").then(r => setSettings(r.data)); };
    window.addEventListener("trams:settings-changed", h);
    return () => window.removeEventListener("trams:settings-changed", h);
  }, []);

  useEffect(() => {
    (async () => {
      const [s, mt, p, mx, f] = await Promise.all([
        api.get("/metrics/summary", { params }),
        api.get("/metrics/mtbf_trend", { params }),
        api.get("/metrics/pareto", { params }),
        api.get("/metrics/subsystem_matrix", { params }),
        api.get("/failures", { params: { ...params, limit: 100, sort: "delay_seconds", order: "desc" } }),
      ]);
      setSummary(s.data); setMtbfTrend(mt.data); setPareto(p.data); setMatrix(mx.data); setFailures(f.data.items);
    })();
  }, [params.start, params.end, params.line, params.subsystem, tick]);

  const c = summary?.current || {};
  const prev = summary?.previous;
  const sett = summary?.settings || settings || {};

  const changeFilter = async (v) => {
    try {
      const body = { ...(settings || {}), mtbf_delay_filter: v };
      const { data } = await api.put("/settings", body);
      setSettings(data);
      window.dispatchEvent(new CustomEvent("trams:settings-changed", { detail: data }));
      toast.success(`MTBF filter: ${FILTER_OPTIONS.find(o => o.value === v)?.label}`);
    } catch (e) { toast.error("Failed to change filter"); }
  };

  const fmt = (k) => {
    const d = new Date(k);
    if (mtbfTrend.granularity === "monthly") return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
    if (mtbfTrend.granularity === "weekly") return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const target = sett.target_mtbf_hours || 100;
  const chartData = mtbfTrend.items.map(m => ({ key: fmt(m.key), MTBF: m.mtbf_hours, "Failure Count": m.events_matched, "All Events": m.events_total }));
  const filterActive = activeFilter !== "all";

  return (
    <div className="space-y-6 pb-8" data-testid="detail-reliability">
      {/* Filter chip bar */}
      <div className="bg-white rounded-xl border border-slate-200/60 kpi-shadow p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${filterActive ? "bg-amber-100 text-[#f59e0b]" : "bg-slate-100 text-slate-500"}`}>
            <FilterIcon className="w-4 h-4" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500">MTBF Delay Filter</div>
            <div className="text-sm text-slate-900 font-semibold">
              Counting failures where delay is <span className="text-[#1e40af]">{FILTER_OPTIONS.find(o => o.value === activeFilter)?.label}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {FILTER_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => changeFilter(opt.value)}
              data-testid={`reliability-filter-${opt.value}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                activeFilter === opt.value
                  ? "bg-[#1e40af] text-white border-[#1e40af] shadow"
                  : "bg-white text-slate-600 border-slate-200 hover:border-[#93c5fd] hover:text-[#1e40af]"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-3 bg-white p-5 rounded-xl border border-slate-200/60 kpi-shadow flex flex-col justify-center items-center">
          <Gauge label="MTBF Reliability" value={c.mtbf_hours} max={Math.max(target * 1.5, c.mtbf_hours || 0)} target={target} unit="Hours" direction="up_good" testId="reliability-gauge" />
        </div>
        <div className="col-span-12 md:col-span-9 grid grid-cols-2 md:grid-cols-3 gap-4">
          <KpiCard testId="kpi-mtbf-r" label={`MTBF (Hours) · ${FILTER_OPTIONS.find(o => o.value === activeFilter)?.label}`} value={c.mtbf_hours} prev={prev?.mtbf_hours} direction="up_good" icon={TrendingUp} iconColor="#22c55e" iconBg="#dcfce7" prevLabel="prev period" />
          <KpiCard testId="kpi-mtbf-count" label="MTBF Failure Count" value={c.mtbf_failure_count} prev={prev?.mtbf_failure_count} direction="down_good" icon={AlertTriangle} iconColor="#f59e0b" iconBg="#fef3c7" prevLabel="prev period" />
          <KpiCard testId="kpi-events-r" label="Total Failure Events" value={c.total_failure_events} prev={prev?.total_failure_events} direction="down_good" icon={Clock} iconColor="#3b82f6" iconBg="#dbeafe" prevLabel="prev period" />
        </div>
      </div>

      <Panel n="1" title={`MTBF Trend · ${mtbfTrend.mtbf_delay_filter_label}`} right={<div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Real-time · updates with filter</div>}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="key" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} unit="h" />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
            <ReferenceLine yAxisId="left" y={target} stroke="#22c55e" strokeDasharray="5 3" label={{ value: `Target ${target}h`, fill: "#22c55e", fontSize: 10, position: "right" }} />
            <Line yAxisId="left" type="monotone" dataKey="MTBF" stroke="#1e40af" strokeWidth={2.5} dot={{ r: 4, fill: "#1e40af" }} />
            <Line yAxisId="right" type="monotone" dataKey="Failure Count" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3, fill: "#f59e0b" }} />
          </LineChart>
        </ResponsiveContainer>
        {filterActive && (
          <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-2">
            <FilterIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <div>
              <strong>Filter active.</strong> Only failures with delay <span className="font-mono">{FILTER_OPTIONS.find(o => o.value === activeFilter)?.label}</span> are counted for MTBF. Total events per bucket shown separately for reference.
            </div>
          </div>
        )}
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Panel n="2" title="Failure Count by Subsystem" className="lg:col-span-7">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={pareto.items} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="subsystem" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} cursor={{ fill: "#f1f5f9" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
              <Bar dataKey="count" name="Failure Count" fill="#1e40af" radius={[6, 6, 0, 0]} maxBarSize={44} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel n="3" title="Top Long-Delay Incidents" className="lg:col-span-5">
          <div className="space-y-2.5 max-h-72 overflow-y-auto trams-scroll pr-1">
            {failures.slice(0, 10).map((r, i) => (
              <div key={r.id} className="flex items-center justify-between gap-3 p-2.5 rounded-lg hover:bg-slate-50 border border-slate-100">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold bg-slate-100 text-slate-700 flex-shrink-0">{i + 1}</div>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-slate-900 truncate">{r.kejadian || "—"}</div>
                    <div className="text-[10px] text-slate-500">{r.tanggal} · {r.equipment}</div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-black text-[#1e40af] tabular-nums">{Math.round((r.delay_seconds || 0) / 60)}</div>
                  <div className="text-[9px] text-slate-400 uppercase">min</div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <SubsystemMatrix
        testId="reliability-subsystem-matrix"
        panelIndex="4"
        title="Matriks Reliability per Subsistem"
        subtitle={`MTBF = Op-Hours ÷ Failure Count (delay: ${mtbfTrend?.mtbf_delay_filter_label || "All Delays"})`}
        target={{ label: `Target ≥ ${matrix.targets?.mtbf_hours ?? target} jam` }}
        items={matrix.items}
        columns={[
          { key: "subsystem", label: "Subsistem", render: (v) => <SubsystemNameCell name={v} colorMap={SUBSYSTEM_COLORS} /> },
          { key: "events", label: "Total Events", align: "right" },
          { key: "matched_events", label: "Failure Count", align: "right", tone: () => "text-amber-700 font-semibold" },
          { key: "mtbf_hours", label: "MTBF (jam)", align: "right", render: (v) => v.toFixed(2), tone: (v) => (v >= (matrix.targets?.mtbf_hours ?? target) ? "text-emerald-700 font-bold" : "text-rose-700 font-bold") },
          { key: "downtime_hours", label: "Downtime (jam)", align: "right", render: (v) => v.toFixed(2) },
          { key: "_status", label: "Status", align: "right",
            render: (_, row) => {
              const goal = matrix.targets?.mtbf_hours ?? target;
              if (row.matched_events === 0) return <StatusPill neutral neutralLabel="Tidak ada kegagalan" />;
              return <StatusPill ok={row.mtbf_hours >= goal} okLabel="On Target" failLabel="Below Target" />;
            } },
        ]}
      />
    </div>
  );
}
